/** Shared grid columns and form fields for global + tenant transformation_header */

export const TRANSFORMATION_HEADER_TYPES = {
  SYNC_FLOW: 'Sync Flow',
  DATA_HUB: 'Data Hub',
};

export const TRANSFORMATION_HEADER_TYPE_OPTIONS = [
  { value: TRANSFORMATION_HEADER_TYPES.SYNC_FLOW, label: 'Sync Flow' },
  { value: TRANSFORMATION_HEADER_TYPES.DATA_HUB, label: 'Data Hub' },
];

/** Legacy rows may have null/empty th_type — treat as Sync Flow in UI */
export function normalizeTransformationHeaderType(value) {
  if (value === TRANSFORMATION_HEADER_TYPES.DATA_HUB) {
    return TRANSFORMATION_HEADER_TYPES.DATA_HUB;
  }
  return TRANSFORMATION_HEADER_TYPES.SYNC_FLOW;
}

/** Main grid: Type + Active only; Process Group / Loop ID / Loop Order stay in add-edit form */
export const transformationHeaderExtendedColumns = [
  {
    field: 'type',
    headerName: 'Type',
    width: 100,
    flex: 0,
    valueFormatter: (value) => normalizeTransformationHeaderType(value),
  },
  {
    field: 'active',
    headerName: 'Active',
    width: 80,
    flex: 0,
    type: 'boolean',
    valueFormatter: (value) => (value === false ? 'No' : 'Yes'),
  },
];

export const transformationHeaderExtendedFormFields = [
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    width: 6,
    required: true,
    options: TRANSFORMATION_HEADER_TYPE_OPTIONS,
    helperText: 'Sync Flow = connector sync mappings; Data Hub = Hub Flow / AI Hub',
    validate: (value) => {
      if (!value) return 'Type is required';
      if (!TRANSFORMATION_HEADER_TYPE_OPTIONS.some((o) => o.value === value)) {
        return 'Type must be Sync Flow or Data Hub';
      }
      return null;
    },
  },
  {
    name: 'processGroup',
    label: 'Process Group',
    width: 6,
    validate: (value) => {
      if (value && value.length > 100) return 'Process Group must be 100 characters or less';
      return null;
    },
  },
  {
    name: 'loopId',
    label: 'Loop ID',
    width: 6,
    show: (formData) => formData.type === TRANSFORMATION_HEADER_TYPES.DATA_HUB,
    validate: (value) => {
      if (value && value.length > 50) return 'Loop ID must be 50 characters or less';
      return null;
    },
  },
  {
    name: 'loopOrder',
    label: 'Loop Order',
    inputType: 'number',
    width: 6,
    show: (formData) => formData.type === TRANSFORMATION_HEADER_TYPES.DATA_HUB,
    inputProps: { min: 0 },
    validate: (value) => {
      if (value === '' || value == null) return null;
      const n = Number(value);
      if (!Number.isInteger(n) || n < 0) return 'Loop Order must be a non-negative integer';
      return null;
    },
  },
  {
    name: 'active',
    label: 'Active',
    type: 'checkbox',
    width: 12,
  },
];

export const transformationHeaderExtendedInitialData = {
  type: TRANSFORMATION_HEADER_TYPES.SYNC_FLOW,
  processGroup: '',
  loopId: '',
  loopOrder: '',
  active: true,
};

/** Build API payload; keeps targetJson/headerCode rules from callers */
export function formatTransformationHeaderPayload(data) {
  const loopOrderRaw = data.loopOrder;
  let loopOrder = null;
  if (loopOrderRaw !== '' && loopOrderRaw != null && loopOrderRaw !== undefined) {
    const parsed = parseInt(loopOrderRaw, 10);
    if (!Number.isNaN(parsed)) loopOrder = parsed;
  }

  return {
    headerCode: data.headerCode || null,
    category: data.category || null,
    subCategory: data.subCategory || null,
    targetJson: data.targetJson ?? null,
    type: normalizeTransformationHeaderType(data.type),
    processGroup: data.processGroup || null,
    loopId: data.loopId || null,
    loopOrder,
    active: data.active !== false,
  };
}

export function prepareTransformationHeaderFormRow(row) {
  return {
    ...row,
    type: normalizeTransformationHeaderType(row.type),
    processGroup: row.processGroup || '',
    loopId: row.loopId || '',
    loopOrder: row.loopOrder != null ? row.loopOrder : '',
    active: row.active !== false,
  };
}

/** UI filter keys: all | sync | hub */
export const TRANSFORMATION_HEADER_TYPE_FILTER = {
  ALL: 'all',
  SYNC: 'sync',
  HUB: 'hub',
};

export function transformationHeaderTypeFilterToApiType(filterKey) {
  if (filterKey === TRANSFORMATION_HEADER_TYPE_FILTER.HUB) {
    return TRANSFORMATION_HEADER_TYPES.DATA_HUB;
  }
  if (filterKey === TRANSFORMATION_HEADER_TYPE_FILTER.SYNC) {
    return TRANSFORMATION_HEADER_TYPES.SYNC_FLOW;
  }
  return null;
}

export function defaultTypeForHeaderFilter(filterKey) {
  const apiType = transformationHeaderTypeFilterToApiType(filterKey);
  return apiType || TRANSFORMATION_HEADER_TYPES.SYNC_FLOW;
}

export function filterTransformationHeadersByType(rows, filterKey) {
  if (!Array.isArray(rows)) return [];
  if (!filterKey || filterKey === TRANSFORMATION_HEADER_TYPE_FILTER.ALL) {
    return rows;
  }
  const want = transformationHeaderTypeFilterToApiType(filterKey);
  return rows.filter((row) => normalizeTransformationHeaderType(row.type) === want);
}

/** Map fixed API type → filter key used by list screens */
export function filterKeyForFixedType(fixedType) {
  if (fixedType === TRANSFORMATION_HEADER_TYPES.DATA_HUB) {
    return TRANSFORMATION_HEADER_TYPE_FILTER.HUB;
  }
  if (fixedType === TRANSFORMATION_HEADER_TYPES.SYNC_FLOW) {
    return TRANSFORMATION_HEADER_TYPE_FILTER.SYNC;
  }
  return TRANSFORMATION_HEADER_TYPE_FILTER.ALL;
}

/**
 * When a screen is locked to Sync Flow or Data Hub, hide the Type field
 * and (optionally) Loop fields already gated by type.
 */
export function transformationHeaderFormFieldsForFixedType(fixedType) {
  if (!fixedType) {
    return transformationHeaderExtendedFormFields;
  }
  return transformationHeaderExtendedFormFields
    .filter((field) => field.name !== 'type')
    .map((field) => {
      if (field.name === 'loopId' || field.name === 'loopOrder') {
        return {
          ...field,
          show: () => fixedType === TRANSFORMATION_HEADER_TYPES.DATA_HUB,
        };
      }
      return field;
    });
}

/** Hide Type column when the screen is already scoped to one type */
export function transformationHeaderColumnsForFixedType(fixedType) {
  if (!fixedType) {
    return transformationHeaderExtendedColumns;
  }
  return transformationHeaderExtendedColumns.filter((col) => col.field !== 'type');
}

export function countTransformationHeadersByType(rows) {
  const counts = {
    [TRANSFORMATION_HEADER_TYPE_FILTER.ALL]: 0,
    [TRANSFORMATION_HEADER_TYPE_FILTER.SYNC]: 0,
    [TRANSFORMATION_HEADER_TYPE_FILTER.HUB]: 0,
  };
  if (!Array.isArray(rows)) return counts;
  counts[TRANSFORMATION_HEADER_TYPE_FILTER.ALL] = rows.length;
  rows.forEach((row) => {
    const t = normalizeTransformationHeaderType(row.type);
    if (t === TRANSFORMATION_HEADER_TYPES.DATA_HUB) {
      counts[TRANSFORMATION_HEADER_TYPE_FILTER.HUB] += 1;
    } else {
      counts[TRANSFORMATION_HEADER_TYPE_FILTER.SYNC] += 1;
    }
  });
  return counts;
}
